import { useEffect } from "react";
import { useGetPersonInfo } from "../../api/PersonApi";
import { ReportSummaryItemApi } from "../../api/Reports";
import CopyIcon from "../../shared-components/CopyIcon";
import {
  buildAddresses,
  buildEducation,
  buildEmails,
  buildJobHistory,
  buildNames,
  buildPhones,
  calculateAge,
} from "./helpers";
import { classNames } from "../../utils";

export type PersonInfoItemProps = {
  label: string;
  items: string[] | string[][];
  collapsable?: boolean;
};

export function PersonItem({
  text,
  hasCopy = false,
  textToCopy,
}: {
  text: string;
  hasCopy?: boolean;
  textToCopy?: string;
}) {
  return (
    <div className="flex justify-between">
      <span className="block leading-5 font-semibold text-gray-600">
        {text}
      </span>
      {hasCopy && <CopyIcon textToCopy={textToCopy || text} />}
    </div>
  );
}

export const PersonInfoItem = ({ label, items }: PersonInfoItemProps) => {
  if (items.length === 0) return <></>;

  return (
    <div className="mb-3 text-sm">
      <span className="block mb-0.5 text-gray-500">{label}</span>
      {items &&
        items.map((item: string | string[], idxitem) => {
          if (Array.isArray(item)) {
            return (
              <div key={idxitem}>
                {item.map((info, idx) => (
                  <PersonItem
                    key={idx}
                    text={info}
                    hasCopy={idx === 0}
                    textToCopy={item.join(", ")}
                  />
                ))}
              </div>
            );
          }
          return <PersonItem text={item} hasCopy={true} key={idxitem} />;
        })}
    </div>
  );
};

export function PersonInfoList({ report }: { report: ReportSummaryItemApi }) {
  const { data: personInfo, mutate: getPerson } = useGetPersonInfo();
  useEffect(() => {
    if (report.person_uuid) getPerson(report.person_uuid);
  }, [getPerson, report.person_uuid]);

  const personInfoObjects = personInfo
    ? [
        {
          label: "Screen Company:",
          itemField: [report.company_name ?? "N/A"],
        },
        {
          label: "Email:",
          itemField: buildEmails(personInfo.emails ?? []),
        },
        {
          label: "Name:",
          itemField: buildNames(personInfo.names ?? []),
        },
        {
          label: "Date of Birth:",
          itemField: personInfo.birth_date ? [personInfo.birth_date] : [],
        },
        {
          label: "Age:",
          itemField: personInfo.birth_date
            ? [calculateAge(personInfo.birth_date).toString()]
            : [],
        },
        {
          label: "Phone:",
          itemField: buildPhones(personInfo.phones ?? []),
        },
        {
          label: "Address:",
          itemField: buildAddresses(personInfo.addresses ?? []),
        },
        {
          label: "Job History:",
          itemField: buildJobHistory(personInfo.jobs ?? []),
        },
        {
          label: "Education:",
          itemField: buildEducation(personInfo.degrees ?? []),
        },
      ]
    : [];

  return (
    <>
      {personInfoObjects
        ?.filter((p) => p.itemField.length > 0)
        .map((eachItem, idx) => (
          <div
            key={eachItem.label + idx}
            className={classNames(
              idx === 0 ? "pt-0" : "pt-2 border-t border-gray-200"
            )}
          >
            <PersonInfoItem
              label={eachItem.label}
              items={eachItem.itemField}
              key={eachItem.label + idx}
            />
          </div>
        ))}
    </>
  );
}

export function CandidateInfoList({
  report,
}: {
  report: ReportSummaryItemApi;
}) {
  const { data: personInfo, mutate: getPerson } = useGetPersonInfo();
  useEffect(() => {
    if (report.person_uuid) getPerson(report.person_uuid);
  }, [getPerson, report.person_uuid]);

  const candidateInfoObjects = personInfo
    ? [
        {
          label: "Email:",
          itemField: buildEmails(personInfo.emails ?? []),
        },
        {
          label: "Name:",
          itemField: buildNames(personInfo.names ?? []),
        },
        {
          label: "Date of Birth:",
          itemField: personInfo.birth_date ? [personInfo.birth_date] : [],
        },
        {
          label: "Age:",
          itemField: personInfo.birth_date
            ? [calculateAge(personInfo.birth_date).toString()]
            : [],
        },
        {
          label: "Phone:",
          itemField: buildPhones(personInfo.phones ?? []),
        },
        {
          label: "Address:",
          itemField: buildAddresses(personInfo.addresses ?? []),
        },
        {
          label: "Job History:",
          itemField: buildJobHistory(personInfo.jobs ?? []),
        },
        {
          label: "Education:",
          itemField: buildEducation(personInfo.degrees ?? []),
        },
      ]
    : [];

  return (
    <>
      {candidateInfoObjects
        ?.filter((p) => p.itemField.length > 0)
        .map((eachItem, idx) => (
          <div
            key={eachItem.label + idx}
            className={classNames(
              idx === 0 ? "pt-0" : "pt-2 border-t border-gray-200"
            )}
          >
            <PersonInfoItem
              label={eachItem.label}
              items={eachItem.itemField}
              key={eachItem.label + idx}
            />
          </div>
        ))}
    </>
  );
}
